"use client";

import { useRef } from "react";
import {
  LazyMotion,
  domAnimation,
  m,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";

interface MagneticProps {
  children: React.ReactNode;
  /** How far (px) the cursor can pull the element. Lower = subtler. */
  strength?: number;
  className?: string;
}

const SPRING_CONFIG = { stiffness: 180, damping: 18, mass: 0.4 };

/**
 * Wraps any child in a magnetic hover effect: the element drifts
 * toward the cursor on `mousemove` and springs back on `mouseleave`.
 *
 * Uses Framer Motion's `useMotionValue` + `useSpring` so the animation
 * runs OUTSIDE React's render cycle (no per-frame state updates).
 *
 * Disabled on touch (no hover) and respects prefers-reduced-motion
 * via the parent CSS gate added in globals.css (.magnetic-hit).
 */
export function Magnetic({ children, strength = 18, className }: MagneticProps) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, SPRING_CONFIG);
  const sy = useSpring(y, SPRING_CONFIG);
  const tx = useTransform(sx, (v) => `${v}px`);
  const ty = useTransform(sy, (v) => `${v}px`);

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    x.set(px * strength * 2);
    y.set(py * strength * 2);
  }

  function onLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ x: tx, y: ty }}
        className={`magnetic-hit inline-block ${className ?? ""}`}
      >
        {children}
      </m.div>
    </LazyMotion>
  );
}
